import { useEffect, useState } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { Button, Input, Modal, Select } from '@react-apps/design-system';
import {
  describeOfferRegisterError,
  isUuid,
  type OfferRegisterField,
} from './offer-errors';
import type {
  GatewayOfferIds,
  GatewayOfferSummary,
  OfferOption,
} from './offers-api.datasource';
import {
  OFFER_PROVIDER_CALL_KEY,
  useLookupGatewayOffer,
  useOfferProviderAccounts,
  useRegisterOffer,
} from './use-offer-search';

/** Depois disto o indicador avisa que o provedor pode levar até um minuto. */
const SLOW_LOOKUP_MS = 5000;

const PROVIDER_LABEL: Record<string, string> = {
  guru: 'Guru',
  hotmart: 'Hotmart',
  eduzz: 'Eduzz',
};

interface RegisterOfferModalProps {
  open: boolean;
  onClose: () => void;
  onRegistered: (offer: OfferOption) => void;
}

/**
 * Cadastra no Sales a oferta que ainda não vendeu, **conferindo no provedor**:
 * o operador vê o que o Guru diz da oferta e confirma antes de gravar. O
 * cadastro aqui não cria nada no Guru — o aviso do topo existe por isso.
 *
 * Os ids começam vazios: o UUID da busca pode ser da oferta ou do produto, e
 * só o operador sabe qual.
 *
 * Com chamada ao provedor em voo o modal não fecha: fechado, o form desmonta e
 * a oferta seria gravada sem voltar ao campo.
 */
export function RegisterOfferModal({
  open,
  onClose,
  onRegistered,
}: RegisterOfferModalProps) {
  const busy = useIsMutating({ mutationKey: OFFER_PROVIDER_CALL_KEY }) > 0;
  return (
    <Modal
      open={open}
      // Vale para o Esc, o fundo e o X do título de uma vez.
      onClose={busy ? () => {} : onClose}
      title="Cadastrar oferta"
      size="lg"
    >
      <RegisterOfferForm onRegistered={onRegistered} onClose={onClose} />
    </Modal>
  );
}

type FieldErrors = Partial<Record<OfferRegisterField | 'product', string>>;

function RegisterOfferForm({
  onRegistered,
  onClose,
}: Omit<RegisterOfferModalProps, 'open'>) {
  const accounts = useOfferProviderAccounts();
  const lookup = useLookupGatewayOffer();
  const register = useRegisterOffer();
  const [ids, setIds] = useState<GatewayOfferIds>({
    providerAccountId: '',
    externalProductId: '',
    externalOfferId: '',
  });
  const [summary, setSummary] = useState<GatewayOfferSummary | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSlow, setIsSlow] = useState(false);

  const isBusy = lookup.isPending || register.isPending;
  useEffect(() => {
    if (!isBusy) return;
    const timer = setTimeout(() => setIsSlow(true), SLOW_LOOKUP_MS);
    return () => {
      clearTimeout(timer);
      setIsSlow(false);
    };
  }, [isBusy]);

  const account = accounts.data?.find((a) => a.id === ids.providerAccountId);
  const providerName = account
    ? (PROVIDER_LABEL[account.provider] ?? account.provider)
    : 'provedor da conta';

  const showError = (err: unknown) => {
    const { field, message } = describeOfferRegisterError(err);
    setErrors({ [field]: message });
    // Erro de campo só se vê no passo dos ids.
    if (field !== 'form') setSummary(null);
  };

  const trimmedIds = (): GatewayOfferIds => ({
    providerAccountId: ids.providerAccountId,
    externalProductId: ids.externalProductId.trim(),
    externalOfferId: ids.externalOfferId.trim(),
  });

  const search = () => {
    const next: FieldErrors = {};
    if (!ids.providerAccountId) next.account = 'Escolha a conta do provedor.';
    if (!isUuid(ids.externalProductId)) {
      next.product = 'Cole o id completo (UUID) do produto.';
    }
    if (!isUuid(ids.externalOfferId)) {
      next.offer = 'Cole o id completo (UUID) da oferta.';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    lookup.mutate(trimmedIds(), {
      onSuccess: (result) => {
        // Já está no Sales: a tela não oferece cadastrar de novo.
        if (result.registeredOffer) onRegistered(result.registeredOffer);
        else setSummary(result.summary);
      },
      onError: showError,
    });
  };

  const confirm = () => {
    register.mutate(trimmedIds(), {
      onSuccess: onRegistered,
      onError: showError,
    });
  };

  const busyLabel = isSlow
    ? 'O provedor pode levar até um minuto…'
    : 'Consultando o provedor…';

  return (
    <div className="space-y-5">
      <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
        Os ids serão buscados no {providerName}. A oferta precisa existir lá
        antes de ser cadastrada aqui.
      </p>

      {errors.form && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {errors.form}
        </p>
      )}

      {summary ? (
        <SummaryStep
          summary={summary}
          providerName={providerName}
          busy={register.isPending}
          busyLabel={busyLabel}
          onConfirm={confirm}
          onBack={() => {
            setSummary(null);
            setErrors({});
          }}
        />
      ) : (
        <form
          noValidate
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            search();
          }}
        >
          <Select
            id="register-offer-account"
            label="Conta do provedor"
            placeholder={
              accounts.isLoading
                ? 'Carregando contas…'
                : accounts.isError
                  ? 'Não foi possível carregar as contas'
                  : accounts.data?.length === 0
                    ? 'Nenhuma conta cadastrada no Sales'
                    : 'Escolha a conta'
            }
            options={(accounts.data ?? []).map((a) => ({
              value: a.id,
              label: `${a.displayName} (${PROVIDER_LABEL[a.provider] ?? a.provider})`,
            }))}
            value={ids.providerAccountId}
            error={errors.account}
            disabled={!accounts.data?.length || isBusy}
            onChange={(e) =>
              setIds({ ...ids, providerAccountId: e.target.value })
            }
          />
          <Input
            id="register-offer-product"
            label="Id do produto"
            autoComplete="off"
            value={ids.externalProductId}
            error={errors.product}
            disabled={isBusy}
            onChange={(e) =>
              setIds({ ...ids, externalProductId: e.target.value })
            }
          />
          <Input
            id="register-offer-offer"
            label="Id da oferta"
            autoComplete="off"
            value={ids.externalOfferId}
            error={errors.offer}
            disabled={isBusy}
            onChange={(e) =>
              setIds({ ...ids, externalOfferId: e.target.value })
            }
          />
          <div className="flex items-center justify-end gap-3">
            {lookup.isPending && (
              <span
                role="status"
                className="text-(--color-text-secondary) text-sm"
              >
                {busyLabel}
              </span>
            )}
            <Button
              type="button"
              variant="ghost"
              disabled={isBusy}
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isBusy}>
              Buscar no provedor
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function SummaryStep({
  summary,
  providerName,
  busy,
  busyLabel,
  onConfirm,
  onBack,
}: {
  summary: GatewayOfferSummary;
  providerName: string;
  busy: boolean;
  busyLabel: string;
  onConfirm: () => void;
  onBack: () => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-(--color-text-primary) text-sm font-medium">
        É esta a oferta?
      </p>
      <dl className="grid gap-3 text-sm sm:grid-cols-2">
        <SummaryField label="Nome" value={summary.name} />
        <SummaryField label="Valor" value={formatValue(summary)} />
        <SummaryField
          label="Situação"
          value={summary.isActive ? 'Ativa' : `Inativa no ${providerName}`}
        />
        <SummaryField label="Ciclos" value={formatCycles(summary.cycles)} />
        <SummaryField label="Cobrança" value={formatInterval(summary)} />
      </dl>
      {!summary.isActive && (
        <p className="text-(--color-text-secondary) text-sm">
          A oferta está inativa no {providerName}. O cadastro segue mesmo assim.
        </p>
      )}
      <div className="flex items-center justify-end gap-3">
        {busy && (
          <span role="status" className="text-(--color-text-secondary) text-sm">
            {busyLabel}
          </span>
        )}
        <Button type="button" variant="ghost" disabled={busy} onClick={onBack}>
          Não
        </Button>
        <Button type="button" disabled={busy} onClick={onConfirm}>
          Sim, é esta oferta
        </Button>
      </div>
    </div>
  );
}

function SummaryField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-(--color-text-secondary) text-xs">{label}</dt>
      <dd className="text-(--color-text-primary)">{value}</dd>
    </div>
  );
}

function formatValue({ value, currency }: GatewayOfferSummary): string {
  if (value === null) return '—';
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency ?? 'BRL',
    }).format(value);
  } catch {
    // Moeda que o Intl não conhece: o número cru com o código.
    return `${value.toFixed(2)} ${currency ?? ''}`.trim();
  }
}

function formatCycles(cycles: number | null): string {
  if (cycles === null) return '—';
  return cycles === 1 ? '1 ciclo' : `${cycles} ciclos`;
}

function formatInterval({
  interval,
  intervalType,
}: GatewayOfferSummary): string {
  if (interval === null || !intervalType) return '—';
  const unit = INTERVAL_UNITS[intervalType];
  // Unidade que o Guru venha a mandar e não está no mapa: crua, sem sumir.
  if (!unit) return `A cada ${interval} ${intervalType}`;
  return `A cada ${interval} ${interval === 1 ? unit[0] : unit[1]}`;
}

/** `plan.interval_type` do Guru → singular e plural. */
const INTERVAL_UNITS: Record<string, [string, string]> = {
  day: ['dia', 'dias'],
  week: ['semana', 'semanas'],
  month: ['mês', 'meses'],
  year: ['ano', 'anos'],
};
